/**
 * Unified Auth Provider using Supabase's SessionContextProvider
 * Created: 2025-07-08 - Replaces custom AuthProvider to fix auth.uid() null issues
 * Updated: 2025-07-09 - Added comprehensive session debugging and validation
 * Uses Supabase's native session management to ensure proper database authentication context
 */

import React, { createContext, useContext, ReactNode, useState, useEffect, useCallback } from "react";
import { Session, User } from "@supabase/supabase-js";
import { useSession, useUser } from '@supabase/auth-helpers-react';
import { useSellerRoleCheck } from "@/hooks/seller/useSellerRoleCheck";
import { debugSession, validateSessionForDatabase, refreshSessionIfNeeded } from "@/utils/sessionDebug";

// Define the AuthContextType with proper nullable types
interface AuthContextType {
  session: Session | null;
  user: User | null;
  isLoading: boolean;
  error: Error | null;
  signOut: () => Promise<void>;
  isSeller: boolean;
  refreshSellerStatus: () => Promise<boolean>;
  validateSession: () => Promise<{ valid: boolean; issues: string[] }>;
  refreshSession: () => Promise<{ refreshed: boolean; error?: string }>;
  sessionDebugInfo: any;
}

// Create context with undefined as initial value
const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const UnifiedAuthProvider = ({ children }: { children: ReactNode }) => {
  // Use Supabase's native session and user hooks
  const session = useSession();
  const user = useUser();
  
  // Local state for additional functionality
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isSeller, setIsSeller] = useState(false);
  const [sessionDebugInfo, setSessionDebugInfo] = useState<any>(null);
  
  // Use the useSellerRoleCheck hook to get the checkSellerRole function
  const { checkSellerRole } = useSellerRoleCheck();

  // Session validation and refresh functions
  const validateSession = useCallback(async () => {
    console.log('🔍 Validating session...');
    const result = await validateSessionForDatabase();
    setSessionDebugInfo(result);
    return result;
  }, []);

  const refreshSession = useCallback(async () => {
    console.log('🔄 Refreshing session...');
    return await refreshSessionIfNeeded();
  }, []);

  // Initialize seller status when session changes - simplified for faster loading
  useEffect(() => {
    const initializeSession = async () => {
      console.log('🚀 Session changed, initializing...', { 
        hasSession: !!session, 
        userId: session?.user?.id 
      });

      if (!session) {
        setIsSeller(false);
        setIsLoading(false);
        setSessionDebugInfo(null);
        return;
      }
      
      setIsLoading(true);
      
      try {
        // Quick session validation first
        const debugInfo = { valid: true, issues: [] };
        setSessionDebugInfo(debugInfo);
        
        // Check seller role - this is now non-blocking for basic form display
        const sellerStatus = await checkSellerRole(session);
        setIsSeller(sellerStatus);
        
        console.log('✅ Session initialization complete', { 
          sellerStatus, 
          userId: session.user?.id 
        });
        
      } catch (err) {
        console.error("❌ Error during session initialization:", err);
        setIsSeller(false);
        setError(err as Error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeSession();
  }, [session, checkSellerRole]);

  // Handle localStorage updates when session changes
  useEffect(() => {
    if (session?.user?.id) {
      localStorage.setItem('userId', session.user.id);
      console.log('User ID stored in localStorage:', session.user.id);
    } else {
      localStorage.removeItem('userId');
    }
  }, [session]);

  // Function to refresh seller status
  const refreshSellerStatus = useCallback(async (): Promise<boolean> => {
    try {
      if (!session) {
        setIsSeller(false);
        return false;
      }
      
      const sellerStatus = await checkSellerRole(session);
      setIsSeller(sellerStatus);
      return sellerStatus;
    } catch (error: any) {
      console.error("Error refreshing seller status:", error);
      return false;
    }
  }, [checkSellerRole, session]);

  const signOut = async () => {
    try {
      console.log('🚪 Starting logout process...');
      
      // Import supabase client only when needed to avoid circular dependencies
      const { supabase } = await import("@/integrations/supabase/client");
      
      // Always clear local storage first (defensive programming)
      localStorage.removeItem('userId');
      console.log('🧹 Local storage cleared');
      
      // Try to sign out with local scope (doesn't require server call)
      // This clears the local session immediately
      try {
        await supabase.auth.signOut({ scope: 'local' });
        console.log('✅ Local session cleared successfully');
      } catch (localError: any) {
        // If local signout fails, log but continue
        console.warn('⚠️ Local signout warning:', localError.message);
      }
      
      // Optionally try global signout (may fail with 403 if session expired)
      // We do this as a best effort but don't block on it
      try {
        await supabase.auth.signOut({ scope: 'global' });
        console.log('✅ Global session revoked successfully');
      } catch (globalError: any) {
        // Expected to fail with 403 if session already expired
        if (globalError.status === 403 || globalError.message?.includes('session_not_found')) {
          console.log('ℹ️ Session already expired on server (expected)');
        } else {
          console.warn('⚠️ Global signout warning:', globalError.message);
        }
      }
      
      // Reset local state
      setIsSeller(false);
      setError(null);
      setSessionDebugInfo(null);
      console.log('🔄 Local auth state reset');
      
      // Force navigation to auth page
      window.location.href = '/auth';
      console.log('🚀 Redirecting to auth page...');
      
    } catch (error: any) {
      // Critical error in logout process
      console.error("❌ Critical error during logout:", error);
      
      // Still try to clean up and redirect even if something fails
      localStorage.removeItem('userId');
      setIsSeller(false);
      setError(null);
      
      // Force redirect as last resort
      window.location.href = '/auth';
    }
  };

  // Create a stable context value to prevent unnecessary re-renders
  const contextValue: AuthContextType = {
    session, 
    user,
    isLoading, 
    error, 
    signOut, 
    isSeller, 
    refreshSellerStatus,
    validateSession,
    refreshSession,
    sessionDebugInfo
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context with proper error handling
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within a UnifiedAuthProvider");
  }
  return context;
};